import { chakra, Flex, Icon, Text, VStack } from '@chakra-ui/react'
import React from 'react'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { Toggle } from '@mangovoice/mango-ui'

import {
  DependentSectionPanel,
  MenuItemAlertContentWrapper,
  MenuItemHeader,
  MenuItemPrimaryContentWrapper,
  MenuItemWrapper,
  ToggleWrapper,
} from './Shared'

const subLabelColor = 'fg.muted'
// `red.fg` is the legible error-foreground token (same one MangoButton uses for
// error text); `red.subtle` is a subtle surface token that washes out to near-
// invisible on the dark settings background.
const settingItemAlertColor = 'red.fg'
interface SettingItemBaseProps {
  label: string
  subLabel?: string
  icon?: SVGComponent | undefined
  onClick: () => void
  controlContent?: React.ReactNode
  alertContent?: React.ReactNode
  disabled?: boolean
  labelId?: string
  subLabelId?: string
}
const SettingItemBase = ({
  label,
  subLabel,
  icon,
  onClick,
  controlContent,
  alertContent,
  disabled,
  labelId,
  subLabelId,
}: SettingItemBaseProps) => {
  const handleClick = () => {
    if (disabled) {
      return
    }
    onClick()
  }
  const isAlert = alertContent !== undefined
  return (
    <MenuItemWrapper onClick={() => handleClick()} isAlert={isAlert}>
      <MenuItemPrimaryContentWrapper hasAlert={isAlert}>
        <Flex direction={'row'}>
          {icon && (
            <Icon as={icon} boxSize="24px" mr="10px" color="fg.default" />
          )}
          <Flex direction={'column'}>
            <Text
              id={labelId}
              {...textStyles[textStyleKeys.Body2]}
              color="fg.default"
            >
              {label}
            </Text>
            {subLabel && (
              <Text
                id={subLabelId}
                {...textStyles[textStyleKeys.Body4]}
                color={subLabelColor}
              >
                {subLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        {controlContent}
      </MenuItemPrimaryContentWrapper>
      {alertContent && (
        // Align the alert under the label, not the row edge: when a leading
        // icon is present the label starts past it (24px icon + 10px margin),
        // so match that offset; otherwise align flush. Kept here rather than as
        // a shared-wrapper default so icon-less rows don't inherit the indent.
        <MenuItemAlertContentWrapper paddingLeft={icon ? '34px' : '0px'}>
          {alertContent}
        </MenuItemAlertContentWrapper>
      )}
    </MenuItemWrapper>
  )
}
export const SettingLinkAction = (props: SettingItemBaseProps) => {
  return <SettingItemBase {...props} />
}
// Retry is opt-in, but the handler and its label are all-or-nothing: wiring
// `onAlertRetry` without a `retryLabel` would render an empty clickable link, so
// the type requires both together or neither.
export type SettingToggleRetry =
  | { onAlertRetry: () => void; retryLabel: string }
  | { onAlertRetry?: undefined; retryLabel?: undefined }
interface SettingToggleBaseProps extends SettingItemBaseProps {
  isChecked: boolean
  alertMessage?: string | undefined
}
type SettingToggleProps = SettingToggleBaseProps & SettingToggleRetry
export const SettingToggle = (props: SettingToggleProps) => {
  const labelId = React.useId()
  const subLabelId = React.useId()
  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    props.onClick()
  }
  return (
    <SettingItemBase
      {...props}
      labelId={labelId}
      subLabelId={subLabelId}
      controlContent={
        <ToggleWrapper isInHeader={false} onClick={handleSwitchClick}>
          <Toggle
            checked={props.isChecked}
            disabled={props.disabled}
            inputProps={{
              'aria-labelledby': labelId,
              'aria-describedby': props.subLabel ? subLabelId : undefined,
            }}
          />
        </ToggleWrapper>
      }
      alertContent={
        props.alertMessage && (
          <Flex direction="row" align="center" gap="2">
            <Text
              {...textStyles[textStyleKeys.Body4]}
              color={settingItemAlertColor}
            >
              {props.alertMessage}
            </Text>
            {props.onAlertRetry && (
              // Inline text link, sized to the Body4 message rather than a full
              // MangoButton (whose Body2 text + sm padding tower over the alert
              // line). Stop the click from bubbling to the row's own onClick
              // (which would toggle the subscription).
              <chakra.button
                type="button"
                {...textStyles[textStyleKeys.Body4]}
                color="brand.fg"
                textDecoration="underline"
                bg="transparent"
                cursor="pointer"
                // Match HelpLink's affordance: deepen on hover, show a ring on
                // keyboard focus (browser defaults are inconsistent on a styled
                // button).
                _hover={{ color: 'brand.solid' }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: 'brand.solid',
                  outlineOffset: '2px',
                  borderRadius: '2px',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  props.onAlertRetry?.()
                }}
              >
                {props.retryLabel}
              </chakra.button>
            )}
          </Flex>
        )
      }
    />
  )
}
interface SettingToggleDependentSectionProps {
  label: string
  icon?: SVGComponent
  isChecked: boolean
  onClick: () => void
  toggleOnContent: React.ReactNode
  toggleOffContent?: React.ReactNode
}
export const SettingToggleDependentSection = ({
  label,
  icon,
  isChecked,
  onClick: handleClick,
  toggleOnContent,
  toggleOffContent,
}: SettingToggleDependentSectionProps) => {
  const labelId = React.useId()
  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleClick()
  }

  return (
    <VStack gap="16px">
      <MenuItemHeader onClick={handleClick} roundBottomCorners={true}>
        {icon && <Icon as={icon} boxSize="24px" mr="10px" color="fg.default" />}
        <Text id={labelId} {...textStyles[textStyleKeys.Body2]}>
          {label}
        </Text>
        <ToggleWrapper isInHeader onClick={handleSwitchClick}>
          <Toggle
            checked={isChecked}
            disabled={false}
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </ToggleWrapper>
      </MenuItemHeader>
      {isChecked && (
        <DependentSectionPanel>{toggleOnContent}</DependentSectionPanel>
      )}
      {!isChecked && toggleOffContent != undefined && (
        <DependentSectionPanel>{toggleOffContent}</DependentSectionPanel>
      )}
    </VStack>
  )
}
